import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import Text from '@amzn/meridian/text';
import Column from '@amzn/meridian/column';
import Alert from '@amzn/meridian/alert';
import styled from '@emotion/styled';
import { rio } from '@amzn/rio-design-tokens';
import { SeeMoreOnAmazonButton } from 'src/common/buttons/seeMoreOnAmazonButton';
import { AdBlock } from 'src/common/adBlock/AdBlock';
import { ProductCard } from 'src/common/productCard';
import { RedirectingToAmazonPage } from 'src/common/errors';
import { usePageContext } from 'src/hooks';
import { useAnalyticsContext } from 'src/common/metrics';
import { sortProducts } from 'src/pages/storefrontPage/utils';
import { serializeAsins } from 'src/utils/asinListUtils';
import { RetailRoutes, useRetailRedirectUrls } from 'src/utils/links';
import { redirectToAmazonApp } from 'src/utils/redirectUtils';
/** Reason string handed to the redirect for a gated-off / unavailable page load. */
const UNAVAILABLE_REASON = 'multi_asin_page_unavailable';
// CLP "Featured products" side inset, shared by the heading and cards so their left edges align.
const SECTION_INSET = '24px';
// Pins the CLP heading style; the && beats the parent flex column's flex:1 that would stretch it.
const FeaturedHeading = styled.div `
  && {
    flex-grow: 0 !important;
    flex-shrink: 0 !important;
    flex-basis: auto !important;
  }
  padding: 8px 0 8px ${SECTION_INSET};
  & > * {
    font-size: ${rio.semantic.size.font.large} !important;
    font-weight: ${rio.semantic.fontWeight.bold} !important;
    line-height: ${rio.semantic.size.lineHeight.large} !important;
  }
`;
// Responsive grid: auto-fills equal columns at a CLP-like min width (2-up on phones, more when wide).
const FeaturedGrid = styled.ul `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
  // 4px top pad + the heading's 8px bottom pad = the CLP 12px title-to-card gap.
  padding: 4px ${SECTION_INSET} 12px;
  margin: 0;
  list-style: none;
`;
// CLP card: white tile, 12px radius, hairline gray border.
const FeaturedCard = styled.li `
  background: ${({ theme }) => theme.rio.semantic.color.background.primary};
  border: 1px solid ${({ theme }) => theme.rio.base.color.gray[1300]};
  border-radius: 12px;
  overflow: hidden;
`;
export const MultiAsinLandingPage = () => {
    const { multiAsinData, isMalpEnabled, multiAsinAcceptedAsins, bundle } = usePageContext();
    const rumClient = useAnalyticsContext();
    // Fire-once guard for the unavailable event (survives re-renders within one page load).
    const hasLoggedUnavailableRef = useRef(false);
    useEffect(() => {
        if (isMalpEnabled || hasLoggedUnavailableRef.current) {
            return;
        }
        hasLoggedUnavailableRef.current = true;
        rumClient.recordEvent('MultiAsinPageUnavailable', {});
        redirectToAmazonApp(UNAVAILABLE_REASON);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMalpEnabled]);
    // Off-ramp: Amazon PDP for the first accepted ASIN, or marketplace home when the list is empty.
    const offRampUrl = useRetailRedirectUrls([RetailRoutes.DETAIL_PAGE], multiAsinAcceptedAsins[0])[RetailRoutes.DETAIL_PAGE];
    if (!isMalpEnabled) {
        return _jsx(RedirectingToAmazonPage, { reason: UNAVAILABLE_REASON });
    }
    const sortedProducts = sortProducts(multiAsinData.products);
    // Full ordered list; each featured card windows it per-click for the PDP carousel (selectCarouselAsins).
    const renderedAsins = serializeAsins(sortedProducts.map((product) => product.asin));
    return (_jsxs(_Fragment, { children: [_jsx(FeaturedHeading, { children: _jsx(Text, { tag: "h1", type: "h300", "data-font": "display", alignment: "left", children: bundle.getMessage('featuredProducts') }) }), sortedProducts.length > 0 && (_jsx(FeaturedGrid, { role: "list", "data-testid": "storefront-product-list", children: sortedProducts.map((item) => (_jsx(FeaturedCard, { children: _jsx(ProductCard, { productItem: item, isFeatured: true, storefrontAsins: renderedAsins }) }, item.asin))) })), sortedProducts.length === 0 && (_jsxs(Column, { spacingInset: "350", spacing: "400", children: [_jsxs(Alert, { type: "error", "data-testid": "product-list-alert", children: [_jsx(Text, { type: "b200", children: _jsx("b", { children: bundle.getMessage('unableToDisplayProducts') }) }), _jsx(Text, { type: "b200", children: bundle.getMessage('tryAgainLater') })] }), _jsx(SeeMoreOnAmazonButton, { offRampUrl: offRampUrl })] })), _jsx(AdBlock, { offRampUrl: offRampUrl, isDeals: false })] }));
};
